import {useState} from "react";
import {Box, Button, Chip, Menu, MenuItem, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReplayIcon from "@mui/icons-material/Replay";

const dot = (color) => <Box sx={{width: 9, height: 9, borderRadius: "50%", backgroundColor: color, ml: 0.75}} />;

const newTag = (
    <Box
        component="span"
        sx={{
            ml: 0.5, fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
            color: "success.main", backgroundColor: "rgba(18,184,134,0.16)", px: 0.5, py: "1px", borderRadius: "999px",
        }}
    >
        New
    </Box>
);

// Assigned-group chips with the edit-mode diff, rendered as a single position-stable list. Groups
// keep their slot across the edit: originally-assigned groups appear in their committed order — a
// group toggled off is shown in place, dimmed/struck ("about to be removed") with a restore action,
// and restoring it returns it to the same slot. Newly-added groups (flagged `added` → highlighted)
// follow after the originals.
//
// Props:
//   items:   [{id, name, color, state: "current" | "removing", added}]  — in display order
//   addable: [{id, name}]
//   editMode, canRemove, onAdd(id), onRemove(id), onRestore(id)
const GroupChips = ({
    items = [],
    addable = [],
    editMode = false,
    canRemove = false,
    onAdd,
    onRemove,
    onRestore,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);
    const pick = (id) => {
        onAdd(id);
        closeMenu();
    };

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center"}}>
            {items.map((g) =>
                g.state === "removing" ? (
                    <Chip
                        key={g.id}
                        icon={dot(g.color)}
                        label={g.name}
                        variant="outlined"
                        deleteIcon={<ReplayIcon />}
                        onDelete={editMode ? () => onRestore(g.id) : undefined}
                        sx={{
                            borderStyle: "dashed",
                            color: "text.secondary",
                            opacity: 0.6,
                            "& .MuiChip-icon": {order: -1, opacity: 0.5},
                            "& .MuiChip-label": {textDecoration: "line-through"},
                        }}
                    />
                ) : (
                    <Chip
                        key={g.id}
                        icon={dot(g.color)}
                        label={g.added ? <span>{g.name}{newTag}</span> : g.name}
                        variant="outlined"
                        onDelete={editMode && canRemove ? () => onRemove(g.id) : undefined}
                        sx={{
                            borderColor: g.color,
                            color: "text.primary",
                            "& .MuiChip-icon": {order: -1},
                            ...(g.added ? {boxShadow: "0 0 0 2px rgba(18,184,134,0.35)"} : {}),
                        }}
                    />
                )
            )}

            {editMode && (
                <>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openMenu}
                        disabled={addable.length === 0}
                        sx={{textTransform: "none", borderRadius: "999px", border: "1px dashed", borderColor: "divider"}}
                    >
                        Add Group
                    </Button>
                    <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
                        {addable.length === 0 ? (
                            <MenuItem disabled>
                                <Typography sx={{fontSize: "0.85rem", fontStyle: "italic"}}>No additional groups available</Typography>
                            </MenuItem>
                        ) : (
                            addable.map((g) => (
                                <MenuItem key={g.id} onClick={() => pick(g.id)} sx={{fontSize: "0.9rem"}}>
                                    {g.name}
                                </MenuItem>
                            ))
                        )}
                    </Menu>
                </>
            )}
        </Box>
    );
};

export default GroupChips;
